<template>

    <div>
      <h2>Unallocated Shipping Costs</h2>
        <p class="lead">({{ issues.length }}
          {{ issues.length !== 1 ? 'transactions' : 'transaction' }}
          {{ issues.length !== 1 ? 'need' : 'needs'}}
          review)</p>
      <table class="table table-sm table-hover">
        <thead>
        <tr>
          <th>
            Date
          </th>
          <th>
            Memo
          </th>
          <th>
            Amount
          </th>
          <th class="text-end">
            Action
          </th>
        </tr>
        </thead>
        <transition mode="out-in">
          <tbody v-if="loading">
            <tr><td colspan="4" class="p-5"><loading-spinner /></td></tr>
          </tbody>

          <tbody v-else-if="issues.length === 0">
          <tr><td colspan="4"><div class="alert alert-success my-5">
            <i class="bi bi-hand-thumbs-up me-2"></i>
            Looking good!  No issues found.
          </div></td></tr>
          </tbody>

          <tbody v-else>
            <tr v-for="issue in issues" :key="issue.id">
              <td class="small">{{ new Date(issue.transaction_date).toDateString() }}</td>
              <td>{{ issue.transaction_memo }}</td>
              <td><money-block :dollar="issue.transaction_amount" /></td>
              <td class="d-flex gap-3 justify-content-end">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="split(issue)" title="Split">
                  <i class="bi bi-arrows-expand-vertical"></i>
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click.stop="ignore(issue)" title="Ignore">
                  <i class="bi bi-eye-slash"></i>
                </button>
                <pre class="d-none small">{{ issue }}</pre>
              </td>
            </tr>
          </tbody>
        </transition>
      </table>

      <app-modal :modalIn="show" @close="close" modalClass="modal-md">
        <template #header>Split Postage</template>
        <div>
          <div v-if="orders && orders.length > 0">
            <div class="card bg-light mb-3">
              <div class="card-body">
                <div class="small text-muted">{{ new Date(selectedIssue.transaction_date).toDateString() }}</div>
                <div>{{ selectedIssue.transaction_memo }}</div>
                <div class="fw-bold"><money-block :dollar="selectedIssue.transaction_amount"></money-block></div>
              </div>
            </div>
            <p class="text-end small">
              <span v-if="processOrders.length > 0">
                Allocated: <strong>${{ processOrdersTotalAbs.toFixed(2) }}</strong>
                | Remaining: <strong>${{ (Math.abs(selectedIssue.transaction_amount) - processOrdersTotalAbs).toFixed(2) }}</strong>
              </span>
              <span v-else>
                Allocated: $0.00 | Remaining $0.00
              </span>
            </p>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h5 class="mb-0">Select Orders to Apply Shipping</h5>
              <div
                  v-if="processOrders.length > 0"
                  class="small"
                  role="button"
                  @click="toggleGranular">
                <i class="bi me-1" :class="{'bi-check-circle': granular, 'bi-circle': !granular}"></i>
                <span>Custom Amounts</span>
              </div>
            </div>
            <div class="list-group">
              <div v-for="order in orders" :key="order.id"
                   class="list-group-item list-group-item-action"
                   :class="{ active: isOrderSelected(order) }"
                   @click="toggleOrder(order)">
                <div class="row">
                  <div class="col-8">
                    <div>
                      <small :class="isOrderSelected(order) ? 'text-light' : 'text-muted'">{{ new Date(order.order_date).toDateString() }}</small>
                      <money-block :dollar="order.amount_paid" class="ms-2 d-inline-block" />
                    </div>
                    <div class="text-ellipsis">{{ order.item_name }}</div>
                  </div>
                  <div class="col-4 text-end">
                    <template v-if="isOrderSelected(order)">
                      <money-input v-if="granular"
                             class="form-control form-control-sm"
                             :modelValue="getProcessOrderShippingLabel(order.id)"
                             @update:modelValue="onShippingLabelChange(order, $event)"
                             @click.stop />
                      <div v-else>
                        <money-block :dollar="getProcessOrderShippingLabel(order.id)" />
                      </div>
                    </template>
                    <div v-else class="text-muted">-</div>
                  </div>
                </div>
              </div>
            </div>
            <pre class="d-none small">{{ processOrders }}</pre>

            <hr>
            <div class="d-flex gap-3 justify-content-between">
              <button class="btn btn-outline-secondary" @click="close" :disabled="submitting">Cancel</button>
              <button class="btn btn-primary" :disabled="!canSplit || submitting" @click="submitSplit">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ submitting ? 'Applying...' : 'Apply to Orders' }}
              </button>
            </div>

          </div>
          <div v-else-if="searched" class="alert alert-warning">
            <h5>
              All orders have been scanned.
            </h5>
            <div>
              <i class="bi bi-ban me-2"></i>
              None found without shipping information.
            </div>
          </div>
          <div v-else class="alert alert-warning">
            <h5>
              The last 30 days of orders have been scanned.
            </h5>
            <div class="mb-3">
              <i class="bi bi-ban me-2"></i>
              None found without shipping information.
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="loadOlder">
              <i class="bi bi-search me-2"></i>
              Scan older orders
            </button>
          </div>
        </div>
      </app-modal>
    </div>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import MoneyInput from "@/app/components/Utils/MoneyInput.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
const store = useStore()
const issues = ref([])
const loading = ref(true);
const show = ref(false);
const selectedIssue = ref();
const orders = ref([]);
const searched = ref(false)
const granular = ref(false)
const processOrders = ref([])

// Check if an order is selected
function isOrderSelected(order) {
  return processOrders.value.some(o => o.id === order.id);
}

// Get shipping label for a process order by id (returns absolute value for display)
function getProcessOrderShippingLabel(orderId) {
  const order = processOrders.value.find(o => o.id === orderId);
  if (!order) return '0.00';

  if (granular.value) {
    // Return absolute value for display (stored as negative, shown as positive)
    return Math.abs(parseFloat(order.shipping_label) || 0).toFixed(2);
  }

  return order.shipping_label

}

// Distribute transaction amount evenly across selected orders (stores as negative)
function distributeAmount() {
  const count = processOrders.value.length;
  if (count === 0 || !selectedIssue.value) return;

  const totalCents = Math.round(Math.abs(selectedIssue.value.transaction_amount) * 100);
  const baseCents = Math.floor(totalCents / count);
  const remainder = totalCents % count;

  processOrders.value.forEach((order, index) => {
    // Distribute remainder cents to first N orders
    const cents = baseCents + (index < remainder ? 1 : 0);
    // Store as negative value
    order.shipping_label = (-cents / 100).toFixed(2);
  });
}

// Computed: sum of all shipping_label values in processOrders (will be negative)
const processOrdersTotal = computed(() => {
  return processOrders.value.reduce((sum, order) => {
    const val = parseFloat(order.shipping_label) || 0;
    return sum + val;
  }, 0);
});

// Computed: absolute value of total for display
const processOrdersTotalAbs = computed(() => {
  return Math.abs(processOrdersTotal.value);
});

// Computed: check if split is valid (totals match using absolute values)
const canSplit = computed(() => {
  if (!selectedIssue.value || processOrders.value.length === 0) return false;
  const target = Math.abs(parseFloat(selectedIssue.value.transaction_amount) || 0);
  const total = processOrdersTotalAbs.value;
  // Compare with small epsilon for floating point
  return Math.abs(target - total) < 0.001;
});

async function loadIssues() {
  loading.value = true;
  try {
    issues.value = await store.dispatch('eshop/getOrphanPostage');
  } catch (error) {
    alert(error.message || error);
  }
  loading.value = false;
}

async function ignore(issue) {
  try {
    console.log('ignore', {...issue})
    const result = await store.dispatch('eshop/ignoreTransaction', {id: issue.id});
    if (result.changedRows > 0) {
      loadIssues();
    }
  } catch (error) {
    alert(error.message || error);
  }
}

function close() {
  selectedIssue.value = null;
  orders.value = []
  show.value = false;
  searched.value = false;
  granular.value = false;
  processOrders.value = []
}

function toggleOrder(order) {
  const index = processOrders.value.findIndex(o => o.id === order.id);
  if (index > -1) {
    // Remove if already selected
    processOrders.value.splice(index, 1);
  } else {
    // Add clone of order with shipping_label initialized
    processOrders.value.push({ ...order, shipping_label: '0.00' });
  }
  // Recalculate distribution (only in non-granular mode)
  if (!granular.value) {
    distributeAmount();
  }
}

// Toggle granular mode (custom amounts vs even distribution)
function toggleGranular() {
  granular.value = !granular.value;
  // When switching back to even distribution, recalculate
  if (!granular.value) {
    distributeAmount();
  }
}

// Handle manual input change in granular mode (store as negative)
function onShippingLabelChange(order, value) {
  const idx = processOrders.value.findIndex(o => o.id === order.id);
  if (idx > -1) {
    // MoneyInput emits float or null; store as negative
    const absValue = Math.abs(value || 0);
    processOrders.value[idx].shipping_label = (-absValue).toFixed(2);
  }
}

// Submit the split to the API
const submitting = ref(false)
async function submitSplit() {
  if (!canSplit.value || submitting.value) return;

  const payload = {
    transaction: {
      id: selectedIssue.value.id,
      transaction_date: selectedIssue.value.transaction_date,
      transaction_amount: selectedIssue.value.transaction_amount,
      transaction_memo: selectedIssue.value.transaction_memo
    },
    orders: processOrders.value.map(o => ({
      id: o.id,
      order_date: o.order_date,
      item_name: o.item_name,
      shipping_label: parseFloat(o.shipping_label)
    }))
  };

  submitting.value = true;
  try {
    const result = await store.dispatch('eshop/splitPostage', payload);
    if (result.success) {
      close();
      loadIssues(); // Refresh the list
    } else {
      alert(result.error || 'Failed to split postage');
    }
  } catch (error) {
    alert(error.message || 'An error occurred while splitting postage');
  } finally {
    submitting.value = false;
  }
}

async function loadOlder() {
  orders.value = await store.dispatch('eshop/getNoShippingOrders', {date:null});
  searched.value = true
}

async function split(issue) {
  orders.value = await store.dispatch('eshop/getNoShippingOrders', { date: issue.transaction_date});
  selectedIssue.value = issue;
  show.value = true;
  console.log('split', {...issue})
}

onMounted(() => {
  loadIssues()
});

</script>